// frontend/src/hooks/useWebSocket.jsx
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './useAuth';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const { user, isAuthenticated } = useAuth();

  const connect = useCallback(() => {
    // Don't attempt to connect if not authenticated or no tenant
    if (!isAuthenticated || !user?.tenant_id) {
      console.log('WebSocket: Not authenticated or missing tenant');
      return;
    }

    // Check if WebSocket is supported
    if (typeof WebSocket === 'undefined') {
      console.debug('WebSocket not supported in this environment');
      return;
    }

    // Close existing connection if any
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.debug('WebSocket: No token available');
        return;
      }

      const wsUrl = `${import.meta.env.VITE_WS_URL || 'ws://localhost:3015'}?tenantId=${user.tenant_id}&token=${token}`;
      
      console.log('WebSocket: Attempting to connect...');
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket: Connected successfully');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          
          // Handle different message types
          switch (data.type) {
            case 'new_transaction':
              window.dispatchEvent(new CustomEvent('new-transaction', { detail: data.data }));
              break;
            case 'stock_alert':
              window.dispatchEvent(new CustomEvent('stock-alert', { detail: data.data }));
              break;
            case 'payment_confirmation':
              window.dispatchEvent(new CustomEvent('payment-confirmed', { detail: data.data }));
              break;
            default:
              break;
          }
        } catch (err) {
          console.debug('WebSocket: Error parsing message', err);
        }
      };

      wsRef.current.onerror = (error) => {
        // Silently handle WebSocket errors - they're optional for the app
        console.debug('WebSocket: Connection error (optional feature)', error);
        setIsConnected(false);
      };

      wsRef.current.onclose = (event) => {
        console.log(`WebSocket: Disconnected (code: ${event.code})`);
        setIsConnected(false);
        
        // Only attempt to reconnect if not a normal closure and under max attempts
        if (event.code !== 1000 && reconnectAttemptsRef.current < 5) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(5000 * reconnectAttemptsRef.current, 30000);
          console.log(`WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/5)`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= 5) {
          console.log('WebSocket: Max reconnection attempts reached, giving up');
        }
      };
    } catch (error) {
      console.debug('WebSocket: Failed to create connection', error);
      setIsConnected(false);
    }
  }, [isAuthenticated, user]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      // Use 1000 for normal closure
      wsRef.current.close(1000, 'Normal closure');
      wsRef.current = null;
    }
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.debug('WebSocket: Failed to send message', error);
        return false;
      }
    } else {
      console.debug('WebSocket: Not connected, message not sent');
      return false;
    }
  }, []);

  // Auto-connect when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.tenant_id) {
      // Small delay to ensure token is properly set
      const timer = setTimeout(() => {
        connect();
      }, 1000);
      
      return () => {
        clearTimeout(timer);
        disconnect();
      };
    } else {
      disconnect();
    }
  }, [isAuthenticated, user, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []);

  const value = {
    isConnected,
    lastMessage,
    sendMessage,
    disconnect,
    reconnect: connect,
    // Helper to check if WebSocket is available
    isSupported: typeof WebSocket !== 'undefined'
  };

  return React.createElement(
    WebSocketContext.Provider,
    { value },
    children
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}